import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import {
  FILTER_POSTAL_CODE,
  FILTER_DISTANCE_IN_MILES,
  PAGE,
  LOCATION_REDIRECT,
  SORT
} from 'dpl/constants/query_params';
import { pushParams, resetParams } from 'dpl/actions/query_params';
import { debounce } from 'dpl/shared/utils';
import { addToQuery, removeFromQuery } from 'dpl/util/queryString';
import { SORT_NEAR_YOU } from 'dpl/puppy_search_results/utils/constants';
import Input from 'dpl/common/design_system/Input';

const DEFAULT_POSTAL_CODE = '';
const DEFAULT_DISTANCE = '0';

export default function LocationFilter({ title }) {
  const queryParams = useSelector(state => state.queryParams);
  const { preappliedFilters, rootPath } = useSelector(({ server }) => server);

  const [postalCode, setPostalCode] = useState(queryParams[FILTER_POSTAL_CODE]);

  const dispatch = useDispatch();

  function resetLocationFilters() {
    dispatch(resetParams(PAGE));
    dispatch(
      resetParams(FILTER_POSTAL_CODE, FILTER_DISTANCE_IN_MILES, { hash: true })
    );
  }
  const zipCodeRef = useRef();
  const { current: setLocationFilter } = useRef(
    debounce(postal => {
      const params = {
        [FILTER_POSTAL_CODE]: postal,
        // We're only passing along distance_in_miles for analytics purposes
        // since postal codes are appended automatically when ip_location_filter
        // so this is the only reliable way to know if location filtering is being done
        [FILTER_DISTANCE_IN_MILES]: DEFAULT_DISTANCE,
        [SORT]: SORT_NEAR_YOU
      };

      if (preappliedFilters?.stateSlug) {
        const newSearchStr = removeFromQuery(PAGE, window.location.search);

        window.location.href = `${rootPath}/?${LOCATION_REDIRECT}=true&${newSearchStr}#${addToQuery(
          params,
          window.location.hash
        )}`;
      } else {
        dispatch(resetParams(PAGE));
        dispatch(pushParams(params, { hash: true }));
      }
    }, 500)
  );

  function handlePostalCodeChange(postal) {
    setPostalCode(postal);
    if (postal.length === 0) {
      resetLocationFilters();
      dispatch(resetParams(SORT, { hash: true }));
    }
    if (postal.length >= 5) {
      setLocationFilter(postal);
    }
  }

  /*
    HACK: This makes sure if the location queryParams are updated outside of the filter itself,
    such as the clear button in the search results, the filter will match the queryParams value.
  */
  useEffect(() => {
    const updatedPostalCode =
      queryParams[FILTER_POSTAL_CODE] || DEFAULT_POSTAL_CODE;

    if (postalCode && postalCode !== updatedPostalCode) {
      handlePostalCodeChange(updatedPostalCode);
    }
  }, [queryParams[FILTER_POSTAL_CODE]]);

  return (
    <>
      {title && (
        <p className="mb2 font-16 fw-medium md:font-18 md:font-serif">
          {title}
        </p>
      )}
      <p className="font-14 md:font-16 secondary mb6">
        We&apos;ll show you puppies from breeders near you.
      </p>
      <p className="mb2 font-12 secondary">ZIP Code</p>
      <Input
        type="number"
        className="ba b--light-gray br3 pv2 ph3 w-100 w-50-md"
        name="postal-code"
        pattern="[0-9]*"
        value={postalCode}
        placeholder="00000"
        ref={zipCodeRef}
        onChange={e => handlePostalCodeChange(e.target.value)}
      />
    </>
  );
}

LocationFilter.propTypes = {
  title: PropTypes.string
};

LocationFilter.defaultProps = {
  title: null
};
